<script lang="ts" setup>
import { ULink } from '#components'

const props = defineProps({
  authors: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  description: {
    default: '',
    type: String,
  },
  featurebase: {
    default: '',
    type: String,
  },
  isNew: {
    default: false,
    type: Boolean,
  },
  minimized: {
    default: false,
    type: Boolean,
  },
  readingTime: {
    default: '',
    type: String,
  },
  schema: {
    default: () => [],
    type: Array as PropType<AnimationSchemaElement[]>,
  },
  slug: {
    default: '',
    type: String,
  },
  tags: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  title: {
    default: undefined,
    type: String,
  },
  to: {
    default: undefined,
    type: [String, Object] as PropType<RawLocation>,
  },
  trophies: {
    default: () => [],
    type: Array as PropType<BaseTrophy[]>,
  },
  youtubeId: {
    default: '',
    type: String,
  },
})

const { t } = useI18n()
const localePath = useLocalePath()
const enabledTags = useRouteQuery<string>('tags', () => '')

const badges = computed((): UBadge[] => {
  if (!props.tags?.length) {
    return []
  }

  const enableds = enabledTags.value.split(',')

  return props.tags.map((tag: string) => ({
    active: enableds.includes(tag),
    label: changeCase.capitalCase(t(`useCases.tags.${tag}`)),
    tag,
  }))
})

const to = computed(() => {
  if (props.featurebase) {
    return props.featurebase
  }

  if (props.to) {
    return props.to
  }

  return props.slug
    ? localePath({
        name: 'use-cases-slug',
        params: { slug: props.slug },
      })
    : undefined
})
</script>

<template>
  <component
    :is="to ? ULink : 'div'"
    class="gradient-texture-reverse group grid items-center gap-5 overflow-hidden rounded-xl text-left text-gray-700 shadow-md ring-1 ring-gray-200 md:items-start dark:text-gray-300 dark:ring-gray-800"
    :class="{
      'transition-base hover:scale-102 hover:shadow-lg hover:ring-gray-300 dark:hover:ring-gray-700': to,
      'p-5 md:gap-6 md:p-6 lg:grid-cols-2': !minimized,
      'p-4': minimized,
    }"
    :target="featurebase ? '_blank' : undefined"
    :to="to"
  >
    <div
      v-if="!minimized"
      class="ring-texture bg-texture h-full overflow-hidden rounded-lg shadow"
      :class="{ 'hidden sm:block': !youtubeId && schema.length }"
    >
      <YouTubeVideo
        v-if="youtubeId"
        height="100%"
        player-classes="xs:min-h-72 size-full sm:min-h-96 md:min-h-80"
        :src="youtubeId"
        width="100%"
      />

      <AnimationSchema
        v-else-if="schema.length"
        :schema="schema"
      />

      <NuxtImg
        v-else
        class="object-cover object-top"
        :src="`/images/content/use-cases/${slug}.jpg`"
      />
    </div>

    <div
      class="flex flex-col text-balance"
      :class="{
        'gap-3 md:my-2': !minimized,
        'gap-2': minimized,
      }"
    >
      <component
        :is="to ? 'h2' : 'h1'"
        class="font-semibold text-black dark:text-white "
        :class="{
          'text-2xl leading-snug': !minimized,
          'text-sm leading-relaxed': minimized,
        }"
      >
        <span v-html="title" />
      </component>

      <p
        v-if="description"
        :class="{
          'text-lg': !minimized,
          'text-sm': minimized,
        }"
        v-html="description"
      />

      <div
        v-if="trophies.length && !minimized"
        class="mt-3 flex flex-col gap-3"
      >
        <div class="text-sm font-semibold uppercase tracking-tight">
          {{ t('trophies') }}
        </div>

        <div class="flex flex-wrap gap-6">
          <BaseTrophy
            v-for="trophy in trophies"
            :key="trophy.domain"
            :domain="trophy.domain"
            :height="trophy.height"
            :hidden-on-mobile="trophy.hiddenOnMobile"
            :name="trophy.name"
          />
        </div>
      </div>

      <div
        v-if="authors.length || badges.length || readingTime"
        class="flex select-none flex-col gap-6 md:flex-row md:justify-between md:gap-4"
        :class="{ 'mt-2': !minimized }"
      >
        <span
          class="flex items-center gap-2 text-gray-600 dark:text-gray-400"
          :class="{
            'text-xs': minimized,
            'text-base': !minimized,
          }"
        >
          <UTooltip
            v-if="authors.length"
            :text="`${t('writtenBy')} ${changeCase.capitalCase(authors[0])}`"
          >
            <UAvatar
              :alt="authors[0]"
              :size="minimized ? '2xs' : 'sm'"
              :src="`/images/content/authors/${authors[0]}.jpg`"
              :ui="{ rounded: 'rounded-full' }"
            />
          </UTooltip>

          <span v-if="readingTime">{{ `${readingTime.replace('read', t('readingTime'))}` }}</span>

          <span
            v-if="isNew"
            class="-ml-1 hidden items-center gap-1 truncate lg:flex"
          >
            • <span class="text-amber-600 dark:text-amber-400">{{ t('globals.new') }}</span>
          </span>

          <div
            v-if="to && minimized"
            class="flex"
          >
            <UIcon
              class="transition-base size-3.5 -translate-x-4 text-indigo-600 opacity-0 group-focus-within:translate-x-0 group-focus-within:opacity-100 group-hover:translate-x-0 group-hover:opacity-100 group-focus:translate-x-0 group-focus:opacity-100 dark:text-indigo-400"
              :name="getIcon('arrowRight')"
            />
          </div>
        </span>

        <BaseBadges
          v-if="badges.length"
          :badges="badges"
          size="lg"
          :uppercase="false"
        />
      </div>

      <!-- <ButtonYouTube
        v-if="!minimized && youtubeId && !to"
        class="mt-2  self-start"
      /> -->
    </div>
  </component>
</template>

<i18n lang="json">
{
  "en": {
    "readingTime": "read",
    "trophies": "Companies using this feature",
    "writtenBy": "Written by"
  },
  "es": {
    "readingTime": "leído",
    "trophies": "Empresas usando esta funcionalidad",
    "writtenBy": "Escrito por"
  },
  "fr": {
    "readingTime": "de lecture",
    "trophies": "Entreprises utilisant cette fonctionnalité",
    "writtenBy": "Écrit par"
  }
}
</i18n>
